import {
  Control,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetValue,
} from 'react-hook-form';
import {
  Currency,
  OnSelectChange,
  PermissionEnum,
  SelectItemProps,
} from '@ui/src';
import { BaseInfoFormValues, GeneralInfo } from '../../../hoc/individual/types';
import { CorporateGeneralInfo } from '../../../hoc/corporate/types';
import {
  AccountsCurrenciesFormValues,
  AccountsFlowsFormValues,
  AffilatedCompanyFormValues,
  ApplicationType,
  CRSAndFatcaFormValues,
  DictionaryItem,
  ExpectedFundsOriginsFormValues,
} from '../../../hoc/new-application/types';
import { CddFormValues } from '../../../hoc/common/aml/types';
import {
  AmlLog,
  CustomerScore,
  OrganizationType,
  CustomerStatus,
  SupplierCustomersProps,
  ApplicationScheme,
  Questionnarie,
  CustomerScoreUpdateStatus,
  RiskScoreRange,
} from '../../../hoc/common/types';

export interface AmlGeneralInfoTypes {
  clientsActivities?: SelectItemProps[];
  type: OrganizationType;
  isLoading: boolean;
  nextReviewDate?: string;
  auditLogAllowed?: boolean;
  setChangeLogPanelIsOpen: (data: boolean) => void;
  baseInformation?: Partial<GeneralInfo> | Partial<CorporateGeneralInfo>;
}

export interface AMLGeneralInfoWrapperTypes {
  isLoading: boolean;
  children?: React.JSX.Element;
  nextReviewDate?: string;
  title?: string;
  auditLogAllowed?: boolean;
  setChangeLogPanelIsOpen?: (data: boolean) => void;
}

export interface GeneralInfoRowTypes {
  title: string;
  className?: string;
  control?: Control<Questionnarie>;
  type?: 'text' | 'textarea' | 'date';
  name?: keyof Questionnarie;
  value?: string;
  disabled?: boolean;
}

export interface AMLAdditionalInformationTypes {
  control?: Control<Questionnarie>;
  questionnaireFields?: Questionnarie;
  setValue: UseFormSetValue<Questionnarie>;
  currentApplicationScheme?: ApplicationScheme;
  questionnaire?: Questionnarie;
  isDirty?: boolean;
  onResetQuestionnaire?: () => void;
  onUpdateQuestionnaire?: () => void;
  requestInProgress?: boolean;
  status?: CustomerStatus;
  type: OrganizationType;
  errors?: FieldErrors<Questionnarie>;
  clearErrors: UseFormClearErrors<Questionnarie>;
  relationshipToTheCustomerDictionary?: DictionaryItem[];
  countries?: SelectItemProps[];
  sourceOfWealth?: DictionaryItem[];
  baseCurrency?: Currency;
  businessActivities?: DictionaryItem[];
  customerMainClients?: DictionaryItem[];
  salesChannels?: DictionaryItem[];
  customerSourceOfFunds?: DictionaryItem[];
  customerSourceOfWealth?: DictionaryItem[];
  annualIncomes?: DictionaryItem[];
  totalIncomeError?: string;
  setTotalIncomeError: (data: string) => void;
  typeOfAccounts?: DictionaryItem[];
  currencies?: SelectItemProps[];
  setAccountsCurrenciesValues: (data: AccountsCurrenciesFormValues[]) => void;
  accountsCurrenciesValues?: AccountsCurrenciesFormValues[];
  onDeleteAccountCurrencies: (id: string) => void;
  selectedAccountsCurrencyId?: string;
  deleteAccountsCurrenciesInProgress?: boolean;
  setAccountsFlowsValues: (data: AccountsFlowsFormValues[]) => void;
  accountsFlowsValues?: AccountsFlowsFormValues[];
  accountsCurrenciesIsLoading?: boolean;
  accountsCurrenciesIsFetching?: boolean;
  accountsFlowsIsLoading?: boolean;
  accountsFlowsIsFetching?: boolean;
  currenciesList?: Currency[];
  expectedFundsOriginsIsLoading?: boolean;
  expectedFundsOriginsIsFetching?: boolean;
  deleteExpectedFundsOriginsInProgress?: boolean;
  onDeleteExpectedFundsOrigins: (id: string) => void;
  selectedExpectedFundsOriginsId?: string;
  expectedFundsOriginsValues?: ExpectedFundsOriginsFormValues[];
  setExpectedFundsOriginsValues: (
    data: ExpectedFundsOriginsFormValues[],
  ) => void;
  createAccountsCurrenciesIsProgress?: boolean;
  updateAccountsCurrenciesInProgress?: boolean;
  createAccountsFlowsIsProgress?: boolean;
  updateAccountsFlowsInProgress?: boolean;
  createExpectedFundsOriginsInProgress?: boolean;
  updateExpectedFundsOriginsInProgress?: boolean;
  sourceOfIncome?: DictionaryItem[];
  sourceOfWealthForIndividual?: DictionaryItem[];
  selectedCrsAndFatcaId?: string;
  crsAndFatcaIsLoading?: boolean;
  crsAndFatcaIsFetching?: boolean;
  createCrsAndFatcaIsProgress?: boolean;
  updateCrsAndFatcaInProgress?: boolean;
  deleteCrsAndFatcaInProgress?: boolean;
  onDeleteCRSAndFatca: (id: string) => void;
  organizationControl?: Control<Partial<BaseInfoFormValues>>;
  organizationErrors?: FieldErrors<Partial<BaseInfoFormValues>>;
  organizationClearErrors?: UseFormClearErrors<Partial<BaseInfoFormValues>>;
  organizationFields?: Partial<BaseInfoFormValues>;
  organizationSetValue?: UseFormSetValue<Partial<BaseInfoFormValues>>;
  crsAndFatcaValues?: CRSAndFatcaFormValues[];
  setCrsAndFatcaValues: (data: CRSAndFatcaFormValues[]) => void;
  isCof?: boolean;
  affilatedCompaniesValues?: AffilatedCompanyFormValues[];
  setAffilatedCompaniesValues: (data: AffilatedCompanyFormValues[]) => void;
  selectedAffilatedCompanyId?: string;
  affilatedCompaniesIsLoading?: boolean;
  affilatedCompaniesIsFetching?: boolean;
  createAffilatedCompanyInProgress?: boolean;
  updateAffilatedCompanyInProgress?: boolean;
  deleteAffilatedCompanyInProgress?: boolean;
  onDeleteAffilatedCompany: (id: string) => void;
}

export interface CustomerAdditionalInformationContentTypes {
  currentApplicationScheme?: ApplicationScheme;
  control?: Control<Questionnarie>;
  questionnaire?: Questionnarie;
  status?: CustomerStatus;
  selectedFilter: 'all' | 'onlyYes';
  setSelectedFilter: (data: 'all' | 'onlyYes') => void;
  questionnaireFields?: Questionnarie;
  setValue: UseFormSetValue<Questionnarie>;
  errors?: FieldErrors<Questionnarie>;
  clearErrors: UseFormClearErrors<Questionnarie>;
  relationshipToTheCustomerDictionary?: DictionaryItem[];
  countries?: SelectItemProps[];
  sourceOfWealth?: DictionaryItem[];
  baseCurrency?: Currency;
  businessActivities?: DictionaryItem[];
  customerMainClients?: DictionaryItem[];
  salesChannels?: DictionaryItem[];
  customerSourceOfFunds?: DictionaryItem[];
  customerSourceOfWealth?: DictionaryItem[];
  annualIncomes?: DictionaryItem[];
  totalIncomeError?: string;
  setTotalIncomeError: (data: string) => void;
  typeOfAccounts?: DictionaryItem[];
  currencies?: SelectItemProps[];
  setAccountsCurrenciesValues: (data: AccountsCurrenciesFormValues[]) => void;
  accountsCurrenciesValues?: AccountsCurrenciesFormValues[];
  onDeleteAccountCurrencies: (id: string) => void;
  selectedAccountsCurrencyId?: string;
  deleteAccountsCurrenciesInProgress?: boolean;
  setAccountsFlowsValues: (data: AccountsFlowsFormValues[]) => void;
  accountsFlowsValues?: AccountsFlowsFormValues[];
  accountsCurrenciesIsLoading?: boolean;
  accountsCurrenciesIsFetching?: boolean;
  accountsFlowsIsLoading?: boolean;
  accountsFlowsIsFetching?: boolean;
  currenciesList?: Currency[];
  expectedFundsOriginsIsLoading?: boolean;
  expectedFundsOriginsIsFetching?: boolean;
  deleteExpectedFundsOriginsInProgress?: boolean;
  onDeleteExpectedFundsOrigins: (id: string) => void;
  selectedExpectedFundsOriginsId?: string;
  expectedFundsOriginsValues?: ExpectedFundsOriginsFormValues[];
  setExpectedFundsOriginsValues: (
    data: ExpectedFundsOriginsFormValues[],
  ) => void;
  sourceOfIncome?: DictionaryItem[];
  sourceOfWealthForIndividual?: DictionaryItem[];
  selectedCrsAndFatcaId?: string;
  crsAndFatcaIsLoading?: boolean;
  crsAndFatcaIsFetching?: boolean;
  deleteCrsAndFatcaInProgress?: boolean;
  onDeleteCRSAndFatca: (id: string) => void;
  organizationControl?: Control<Partial<BaseInfoFormValues>>;
  organizationErrors?: FieldErrors<Partial<BaseInfoFormValues>>;
  organizationClearErrors?: UseFormClearErrors<Partial<BaseInfoFormValues>>;
  organizationFields?: Partial<BaseInfoFormValues>;
  organizationSetValue?: UseFormSetValue<Partial<BaseInfoFormValues>>;
  crsAndFatcaValues?: CRSAndFatcaFormValues[];
  setCrsAndFatcaValues: (data: CRSAndFatcaFormValues[]) => void;
  isCof?: boolean;
  affilatedCompaniesValues?: AffilatedCompanyFormValues[];
  setAffilatedCompaniesValues: (data: AffilatedCompanyFormValues[]) => void;
  selectedAffilatedCompanyId?: string;
  affilatedCompaniesIsLoading?: boolean;
  affilatedCompaniesIsFetching?: boolean;
  deleteAffilatedCompanyInProgress?: boolean;
  onDeleteAffilatedCompany: (id: string) => void;
}

export interface QuestionTypes {
  question: string;
  active?: boolean;
  control?: Control<Questionnarie>;
  name: keyof Questionnarie;
  additionalName?: keyof Questionnarie;
  additionalList?: SelectItemProps[];
  setValue?: UseFormSetValue<Questionnarie>;
  additionalQuestion?: string;
  textarea?: boolean;
  mainList?: SelectItemProps[];
  answer?: string;
  details?: string;
  disabled?: boolean;
}

export interface AMLRiskScoringTypes {
  onUpdate?: () => void;
  updateInProgress?: boolean;
  updateIsSuccess?: boolean;
  aml?: CustomerScore;
  amlUpdateStatus?: CustomerScoreUpdateStatus;
  calculateAllowed?: boolean;
  status?: CustomerStatus;
  isLoading?: boolean;
  users?: SelectItemProps[];
  manualScoreAllowed?: boolean;
  setManualScoreSidepanelVisible?: (data: boolean) => void;
  setRiskScoreChangeLogsSidepanelVisible?: (data: boolean) => void;
  monthlyTurnover?: SelectItemProps[];
  countries?: SelectItemProps[];
  activePermissions?: PermissionEnum[];
  clientsActivities?: SelectItemProps[];
  blockCollapsed?: boolean;
  deliveryChannels?: SelectItemProps[];
  products?: SelectItemProps[];
  sourcesOfFunds?: SelectItemProps[];
  individualDeliveryChannels?: SelectItemProps[];
  individualProducts?: SelectItemProps[];
  sicCodesDictionaries?: SelectItemProps[];
  corporateDeliveryChannels?: SelectItemProps[];
  corporateProducts?: SelectItemProps[];
  riskScoreRanges?: RiskScoreRange[];
}

export enum RiskScoringTypes {
  GEOGRAPHICAL_RISK = 'geographicalRisk',
  TYPE_OF_CLIENT = 'typeOfClient',
  DELIVERY_CHANNEL = 'deliveryChannel',
  PRODUCTS = 'products',
  MONTHLY_VOLUMES = 'monthlyVolumes',
  SOURCE_OF_FUNDS = 'sourceOfFunds',
  SIC_CODES = 'sicCodes',
  DATE_ON_INCORPORATION = 'dateOfIncorporation',
}

export interface TotalRiskScoringTypes {
  logs?: AmlLog[];
  onUpdate?: () => void;
  updateInProgress?: boolean;
  updateIsSuccess?: boolean;
  aml?: CustomerScore;
  onCollapse: () => void;
  blockCollapsed: boolean;
  amlUpdateStatus?: CustomerScoreUpdateStatus;
  calculateAllowed?: boolean;
  status?: CustomerStatus;
  isLoading?: boolean;
  users?: SelectItemProps[];
  manualScoreAllowed?: boolean;
  setManualScoreSidepanelVisible: (data: boolean) => void;
  changeLogsAvailable?: boolean;
  setRiskScoreChangeLogsSidepanelVisible: (data: boolean) => void;
  riskScoreRanges?: RiskScoreRange[];
}

export interface RiskScoringContentTypes {
  blockCollapsed?: boolean;
  onExpandClick: () => void;
  expandedRows: string[];
  type: OrganizationType;
  setExpandedRows: (data: string[]) => void;
  aml?: CustomerScore;
  countries?: SelectItemProps[];
  clientsActivities?: SelectItemProps[];
  deliveryChannels?: SelectItemProps[];
  products?: SelectItemProps[];
  sourcesOfFunds?: SelectItemProps[];
  sicCodesDictionaries?: SelectItemProps[];
  monthlyTurnover?: SelectItemProps[];
  riskScoreRanges?: RiskScoreRange[];
}

export interface ExpandableRowTypes {
  title: string;
  expandedRows: string[];
  setExpandedRows: (data: string[]) => void;
  name: RiskScoringTypes;
  children?: React.ReactNode;
  totalRisk: number;
  element?: React.JSX.Element;
  riskScoreRanges?: RiskScoreRange[];
}

export interface ExpandableRowContentTypes {
  title?: string;
  list?: SelectItemProps[];
  value: string;
  risk: number | string;
  name?: string;
  withoutOpacity?: boolean;
  officialRiskItems?: { id: string; name: string; value: string }[];
  isEditable?: boolean;
}

export interface AmlComponentProps {
  // onSend: () => void;
  // onDelete: () => void;
  expandedRows: string[];
  setExpandedRows: (value: string[]) => void;
  values?: CustomerScore;
  type?: OrganizationType | ApplicationType;
  riskScoreRanges?: RiskScoreRange[];
  isEditable?: boolean;
}

export interface RealMonthlyVolumesTypes {
  title: string;
  children?: React.JSX.Element;
  monthlyMaxValueIncomingPaymentRangeId?: string;
  monthlyMaxValueOutgoingPaymentRangeId?: string;
  monthlyNumberIncomingTransactionsRangeId?: string;
  monthlyNumberOutgoingTransactionsRangeId?: string;
  monthlyTurnoverRangeId?: string;
  monthlyValueIncomingTransactionsRangeId?: string;
  monthlyValueOutgoingTransactionsRangeId?: string;
  corporate?: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement> | OnSelectChange) => void;
  status?: CustomerStatus;
  saveButtonDisabled?: boolean;
  requestInProgress?: boolean;
  onSaveChanges: () => void;
  montlyTurnoverValues: {
    monthlyTurnover?: SelectItemProps[];
    numberOfIncomingTransactions?: SelectItemProps[];
    numberOfOutgoingTransactions?: SelectItemProps[];
    valueOfIncomingTransactions?: SelectItemProps[];
    valueOfOutgoingTransactions?: SelectItemProps[];
    maxValueOfIncomingPayment?: SelectItemProps[];
    maxValueOfOutgoingPayment?: SelectItemProps[];
    monthlyTurnoverCorporate?: SelectItemProps[];
    numberOfIncomingTransactionsCorporate?: SelectItemProps[];
    numberOfOutgoingTransactionsCorporate?: SelectItemProps[];
    valueOfIncomingTransactionsCorporate?: SelectItemProps[];
    valueOfOutgoingTransactionsCorporate?: SelectItemProps[];
    maxValueOfIncomingPaymentCorporate?: SelectItemProps[];
    maxValueOfOutgoingPaymentCorporate?: SelectItemProps[];
  };
}

export interface RealMonthlyVolumeTypes {
  title: string;
  value?: string;
  // warning?: string;
  list?: SelectItemProps[];
  onUpdateRange: (data: string) => void;
  disabled?: boolean;
}

export interface AMLBusinessPartnersTypes {
  businessPartners?: {
    suppliers?: SupplierCustomersProps[];
    customers?: SupplierCustomersProps[];
  };
  baseCurrency?: string;
  clientScheme?: ApplicationScheme;
  monthlyTurnoverCorporate?: SelectItemProps[];
}

export interface InitialCDDBusinessTypes {
  businessPartners?: {
    suppliers?: SupplierCustomersProps[];
    customers?: SupplierCustomersProps[];
  };
  baseCurrency?: string;
  clientScheme?: ApplicationScheme;
  monthlyTurnoverCorporate?: SelectItemProps[];
  cddFormValues: CddFormValues;
  onChange: (e: React.ChangeEvent<HTMLInputElement> | OnSelectChange) => void;
  currencies?: SelectItemProps[];
}

export interface ManualRiskScoreFormTypes {
  totalRiskScoreLevel?: string;
  riskScoreRanges?: RiskScoreRange[];
  setTotalRiskScoreLevel: (data: string) => void;
  setTotalRiskScore: (data: number) => void;
  totalRiskScore: number;
}

export interface AmlChangeLogTypes {
  amlChangeLog: CustomerScore;
  evenChangeLog?: CustomerScore;
  userName?: string;
  type: OrganizationType | ApplicationType;
  riskScoreRanges?: RiskScoreRange[];
  onCollapse: (data: {
    amlChangeLog?: CustomerScore;
    evenChangeLog?: CustomerScore;
  }) => Promise<{
    amlChangeLogDetails?: CustomerScore;
    evenChangeLogDetails?: CustomerScore;
  }>;
  isLoading?: boolean;
  isFetching?: boolean;
  applicationAmlDetailsIsLoading?: boolean;
  applicationAmlDetailsIsFetching?: boolean;
  selectedChangeLogId?: string;
}

export interface AmlChangeLogDetailsTypes {
  changeLog: CustomerScore;
  type: OrganizationType | ApplicationType;
  className: string;
  riskLevel?: RiskScoreRange;
}

export interface AmlChangeLogValueRowTypes {
  title?: string;
  value: string;
  score: number | string;
  additionalInfo?: { id: string; name: string; value: string }[];
}
